import React, { useEffect, useState } from "react";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import "bootstrap/dist/css/bootstrap.min.css";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const InventoryLocationTransfer = ({ accessOfDepartments }) => {
  const [transferDate, setTransferDate] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [departments, setDepartments] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [locations, setLocations] = useState([]);
  const code = localStorage.getItem('code');
  const name = localStorage.getItem('name');
  const badgeId = localStorage.getItem('badgeId');
  const email = localStorage.getItem('email'); // Retrieve email from localStorage
  const role = localStorage.getItem('role'); // Retrieve email from localStorage
  const token = localStorage.getItem('token')
  const navigate = useNavigate();
  const [rows, setRows] = useState([
    {
      item: null,
      fromLocation: null,
      toLocation: null,
      availableQty: 0,
      transferQty: "",
      locationOptions: []
    }
  ]);
  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments
        .split(',')
        .map((dept) => ({ value: dept.trim(), label: dept.trim() }));
      setDepartments(deptArray);
    }
  }, [accessOfDepartments]);
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchItemOptions = async (accessOfDepartmentsArray) => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getItemOptionsByDepartment`, {
        params: {
          code,
          accessOfDepartments: accessOfDepartmentsArray?.join(','),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setItemOptions(
        response.data.map((item) => ({
          value: item.printName,
          itemId: item.id,
          label: `${item.printName}${item.additionalNames ? ` - [ ${item.additionalNames} ]` : ""}`,
          itemCode: item.itemCode,
          uom: item.uom,
          unit: item.unit
        }))
      );
    } catch (error) {
      console.error("Error fetching item data:", error.message);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiClient.get(SERVER_BASE + "/getLocations", {
        params: { code },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(
        response.data.map((location) => ({
          value: location.location,
          label: location.location
        }))
      );
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchAvailableQuantity = async (item, location, deptOverride) => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getAvailableQuantity`, {
        params: {
          item,
          location,
          status: 'As Stock',
          code,
          department: deptOverride || selectedDepartment?.value,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data.availableQuantity || 0;
    } catch (error) {
      console.error('Error fetching available quantity:', error);
      return 0;
    }
  };
  const fetchLocationsForItem = async (itemId, deptOverride) => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getLocationsByItem`, {
        params: {
          itemId,
          department: deptOverride || selectedDepartment?.value,
          code
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.map((loc) => ({
        value: loc.location,
        label: loc.location
      }));
    } catch (error) {
      console.error("Error fetching item-specific locations:", error);
      return [];
    }
  };

  const handleDepartmentChange = async (selected) => {
    setSelectedDepartment(selected);
    fetchItemOptions([selected.value]);

    // Update available quantity for each row (your existing logic)
    const updatedRows = await Promise.all(
      rows.map(async (row) => {
        if (row.item && row.fromLocation) {
          const qty = await fetchAvailableQuantity(
            row.item.itemId,
            row.fromLocation.value,
            selected.value
          );

          // also refresh location options for this item in the new dept
          const locs = await fetchLocationsForItem(row.item.itemId, selected.value);

          return {
            ...row,
            availableQty: qty,
            locationOptions: locs,
            fromLocation: null,   // reset old selections
            toLocation: null,
            transferQty: ""
          };
        }

        // if no item yet, just reset row
        return {
          ...row,
          locationOptions: [],
          fromLocation: null,
          toLocation: null,
          availableQty: 0,
          transferQty: ""
        };
      })
    );

    setRows(updatedRows);
  };

  const handleRowChange = async (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;

    const item = field === "item" ? value : updated[index].item;
    const fromLocation = field === "fromLocation" ? value : updated[index].fromLocation;

    // 👇 If item is changed
    if (field === "item" && item && selectedDepartment) {
      const locs = await fetchLocationsForItem(item.itemId, selectedDepartment.value);
      updated[index].locationOptions = locs;

      // Reset dependent fields
      updated[index].fromLocation = null;
      updated[index].toLocation = null;
      updated[index].availableQty = 0;
      updated[index].transferQty = "";
    }

    // 👇 If fromLocation changes, recalc available qty
    if (item && fromLocation && selectedDepartment) {
      const qty = await fetchAvailableQuantity(item.itemId, fromLocation.value);
      updated[index].availableQty = qty;

      if (updated[index].transferQty > qty) {
        updated[index].transferQty = qty;
      }
    }

    setRows(updated);
  };

  const handleQtyChange = (index, value) => {
    const updated = [...rows];
    updated[index].transferQty = value;
    setRows(updated);
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        item: null,
        fromLocation: null,
        toLocation: null,
        availableQty: 0,
        transferQty: ""
      }
    ]);
  };

  const removeRow = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic checks
    if (!transferDate) {
      return Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: 'Date is required', timer: 3000, showConfirmButton: false });
    }

    if (!selectedDepartment) {
      return Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: 'Department is required', timer: 3000, showConfirmButton: false });
    }

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (!row.item || !row.fromLocation || !row.toLocation || !row.transferQty) {
        return Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: `Please fill all fields in row ${i + 1}`,
          timer: 3000,
          showConfirmButton: false,
        });
      }

      if (Number(row.transferQty) > Number(row.availableQty)) {
        return Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: `Transfer qty cannot exceed available qty in row ${i + 1}`,
          timer: 3000,
          showConfirmButton: false,
        });
      }
    }

    const payload = {
      transferDate,
      department: selectedDepartment?.value,
      createdBy: name,
      createdByBadge: badgeId,
      centerCode: code,
      remarks,
      items: rows.map((row) => ({
        itemId: row.item?.itemId,
        item: row.item?.value,              // printName
        itemCode: row.item?.itemCode,
        uom: row.item?.uom,
        unit: row.item?.unit,

        fromLocation: row.fromLocation?.value,
        toLocation: row.toLocation?.value,
        availableQty: row.availableQty,
        transferQty: row.transferQty
      }))
    };


    try {
      await apiClient.post(`${SERVER_BASE}/saveLocationTransfer`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Transfer submitted successfully!',
        timer: 3000,
        showConfirmButton: false,
      });
      navigate('../InventoryLocationTransferList')
      setTransferDate("");
      setSelectedDepartment(null);
      setRows([{
        item: null,
        fromLocation: null,
        toLocation: null,
        availableQty: 0,
        transferQty: ""
      }]);

    } catch (error) {
      console.error("Transfer Submit Error:", error);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: 'Failed to submit transfer',
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };


  return (
    <div className="formcontainer mt-5">
      <div className="d-flex justify-content-start align-items-end mb-3 gap-3">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate('../InventoryLocationTransferList')}
        >
          Back
        </button>
        <h4 className="mb-0">Location Transfer</h4>
      </div>


      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Department</label>
            <Select
              options={departments}
              value={selectedDepartment}
              onChange={handleDepartmentChange} // calls async update + availableQty refresh
              placeholder="Select Department"
              isClearable
              required
            />
          </div>
          <div className="col-md-5">
            <label className="form-label">Remarks</label>
            <input
              type="text"
              className="form-control"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}

            />
          </div>
        </div>

        {/* Header Row with Labels */}
        <div className="row fw-bold mb-2">
          <div className="col-md-3">Item</div>
          <div className="col-md-2">From Location</div>
          <div className="col-md-2">Available Qty</div>
          <div className="col-md-2">To Location</div>
          <div className="col-md-2">Transfer Qty</div>
          <div className="col-md-1">Action</div>
        </div>

        {/* Dynamic Input Rows */}
        {rows.map((row, i) => (
          <div className="row align-items-center mb-2" key={i}>
            <div className="col-md-3">
              <Select
                options={itemOptions}
                value={row.item}
                onChange={(val) => handleRowChange(i, "item", val)}
                placeholder="Select Item"
              />
            </div>

            <div className="col-md-2">
              <Select
                options={row.locationOptions}
                value={row.fromLocation}
                onChange={(val) => handleRowChange(i, "fromLocation", val)}
                placeholder="From"
              />
            </div>

            <div className="col-md-2">
              <input
                type="number"
                className="form-control"
                value={row.availableQty}
                disabled
              />
            </div>

            <div className="col-md-2">
              <Select
                options={locations}
                value={row.toLocation}
                onChange={(val) => handleRowChange(i, "toLocation", val)}
                placeholder="To"
              />
            </div>

            <div className="col-md-2">
              <input
                type="number"
                className="form-control"
                min="0"
                max={row.availableQty}
                value={row.transferQty}
                onChange={(e) => handleQtyChange(i, e.target.value)}
              />
            </div>

            <div className="col-md-1 d-flex gap-1">
              {i === rows.length - 1 && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={addRow}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              )}
              {rows.length > 1 && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeRow(i)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>
          </div>
        ))}
        <button type="submit" className="btn btn-success">
          Submit Transfer
        </button>
      </form>
    </div>
  );
};

export default InventoryLocationTransfer;
